<template>
  <div class="card">
    <h5 class="card-header">Profile</h5>
    <div class="card-body">
      <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
        {{ successMessage }}
        <button type="button" class="close" @click="successMessage = null" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <ErrorSummary :error="error" />
      <form ref="form" class="needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
        <div class="form-group">
          <label for="emailAddress">Email Address</label>
          <div class="input-group">
            <input v-model="emailAddress" class="form-control-plaintext" id="emailAddress" readonly />
            <div class="input-group-append">
              <span v-if="emailAddressVerified" class="input-group-text text-success">
                <fa icon="check" />
                <abbr class="pl-1" title="This email address has been verified by its owner.">verified</abbr>
              </span>
              <span v-if="!emailAddressVerified" class="input-group-text text-danger">
                <fa icon="times" />
                <abbr class="pl-1" title="This email address has not been verified by its owner."> unverified</abbr>
              </span>
            </div>
          </div>
          <div v-if="isVerensicsAdministrator || isOrganizationAdministrator">
            <small v-if="!emailAddressVerified" class="form-text text-muted">
              <a v-if="!isResendingWelcomeEmail" href="#" class="btn btn-link btn-sm p-0" @click="resendWelcomeEmail">
                <fa icon="envelope" /> Resend welcome email
              </a>
              <a v-if="isResendingWelcomeEmail" href="#" class="btn btn-link btn-sm p-0 disabled" @click="resendWelcomeEmail">
                <fa icon="circle-notch" spin /> Sending...
              </a>
            </small>
          </div>
        </div>
        <div class="form-group">
          <label for="fullName">Full Name</label>
          <input
            v-model="fullName"
            type="text"
            class="form-control"
            id="fullName"
            placeholder="Full Name"
            required
            :disabled="loadingMessage"
            @keyup.enter="submit"
          />
          <div class="invalid-feedback">
            Please provide a full name.
          </div>
        </div>
        <div class="form-group">
          <label for="mobilePhoneNumber">Mobile Phone</label>
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text" id="inputGroupPrepend">+1</span>
            </div>
            <input
              v-model="editorMobilePhoneNumber"
              type="tel"
              pattern="^\d{3}[\-]?\d{3}[\-]?\d{4}$"
              class="form-control"
              id="mobilePhoneNumber"
              placeholder="555-555-5555"
              :disabled="loadingMessage"
              @keyup.enter="submit"
            />
            <div class="input-group-append">
              <span v-if="mobilePhoneNumberVerified" class="input-group-text text-success" id="inputGroupPrepend">
                <fa icon="check" />
                <abbr class="pl-1" title="This mobile phone number has been verified by its owner.">verified</abbr>
              </span>
              <span v-if="!mobilePhoneNumberVerified" class="input-group-text text-danger" id="inputGroupPrepend">
                <fa icon="times" />
                <abbr class="pl-1" title="This mobile phone number has not been verified by its owner."> unverified</abbr>
              </span>
            </div>
            <div class="invalid-feedback">
              Please provide a valid mobile phone number.
            </div>
          </div>
        </div>
      </form>
    </div>
    <div class="card-footer">
      <button type="button" class="btn btn-primary btn-block" @click="submit" :disabled="loadingMessage" @keyup.enter="signIn">
        <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        {{ loadingMessage ? loadingMessage : "Save" }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ErrorSummary from "@/components/ErrorSummary.vue";

export default {
  components: {
    ErrorSummary,
  },
  props: {
    organizationId: String,
    userId: String,
  },
  data() {
    return {
      loadingMessage: null,
      formWasValidated: false,
      successMessage: null,
      error: null,

      emailAddress: null,
      emailAddressVerified: null,
      fullName: null,
      mobilePhoneNumber: null,
      mobilePhoneNumberVerified: null,

      isResendingWelcomeEmail: false,
    };
  },
  computed: {
    ...mapGetters("role", ["isVerensicsAdministrator", "isOrganizationAdministrator"]),
    editorMobilePhoneNumber: {
      get() {
        if (!this.mobilePhoneNumber) return null;
        var value = this.mobilePhoneNumber.replace(/^\+1/, ""); // remove prefix
        if (value.length >= 3) value = value.substr(0, 3) + "-" + value.substr(3); // insert dash
        if (value.length >= 7) value = value.substr(0, 7) + "-" + value.substr(7); // insert dash
        return value;
      },
      set(newValue) {
        this.mobilePhoneNumber = ""; // force input value update
        this.mobilePhoneNumber = "+1" + (newValue || "").replace(/[^0-9]/g, ""); // add +1 and remove non-numeric characters
      },
    },
  },
  created() {
    this.load();
  },
  watch: {
    organizationId() {
      this.load();
    },
    userId() {
      this.load();
    },
  },
  methods: {
    load() {
      this.loadingMessage = "Loading...";
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.userId}/profile`)
        .then(res => {
          this.emailAddress = res.data.emailAddress;
          this.emailAddressVerified = res.data.emailAddressVerified;
          this.fullName = res.data.fullName;
          this.mobilePhoneNumber = res.data.mobilePhoneNumber;
          this.mobilePhoneNumberVerified = res.data.mobilePhoneNumberVerified;
        })
        .catch(() => this.$router.push(`/organizations/${this.organizationId}/users`))
        .finally(() => (this.loadingMessage = null));
    },
    reset() {
      this.loadingMessage = null;
      this.formWasValidated = false;
      this.successMessage = null;
      this.error = null;
    },
    submit() {
      const payload = {
        fullName: this.fullName,
        mobilePhoneNumber: this.mobilePhoneNumber,
      };

      // reset
      this.reset();

      // validations
      if (!this.$refs.form.checkValidity()) {
        this.formWasValidated = true;
        return;
      }

      // api
      this.loadingMessage = "Saving...";
      this.$axios
        .put(`/v1/organizations/${this.organizationId}/users/${this.userId}/profile`, payload)
        .then(() => (this.successMessage = "Save successful!"))
        .catch(err => (this.error = err))
        .finally(() => (this.loadingMessage = null));
    },
    resendWelcomeEmail() {
      this.isResendingWelcomeEmail = true;
      this.$axios
        .post(`/v1/organizations/${this.organizationId}/users/${this.userId}/profile/email/verify`)
        .catch(err => (this.error = err))
        .finally(() => setTimeout(() => (this.isResendingWelcomeEmail = false), 2000));
    },
  },
};
</script>
